import { useState, useEffect, useRef } from 'react';
import { zapPaths } from '../zapPaths';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/tailwind-component/ui/dialog';
import { Button } from '../../../../../components/tailwind-component/ui/button';
import { Progress } from '../../../../../components/tailwind-component/ui/progress';
// import { StepScope } from './wizard/StepScope';
import { StepSelectPosts } from './wizard/StepSelectPosts';
import { StepConfigureRules, StepConfigureRulesHandle } from './wizard/StepConfigureRules';
import { StepGoal } from './wizard/StepGoal';
import { StepGrowth } from './wizard/StepGrowth';
import { StepGoalSetup } from './wizard/StepGoalSetup';
import { StepHelpButton, StepHelpDialog, StepGuideDialog } from './wizard/StepHelp';
import { getStepHelp, StepHelpScreen } from './wizard/stepHelpContent';
import {
  ZapGoalId,
  ZapGoalAnswers,
  emptyGoalAnswers,
  getGoalTemplate,
  goalQuestions,
  isGoalQuestionAnswered,
  buildRuleFromGoal,
  buildAutomationName,
  summariseGoalRule,
  inferGoalFromRules,
  formSavePayload,
  formButtonLabel,
} from './zapGoalTemplates';
import { ZapFormService } from '../../../../../core/services/zap-form.service';
import { BASE_URL_UI } from '../../../../../config/config';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from './ui/alert-dialog';
// import { AutomationScope } from './AutomationDashboard';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// import { toast } from 'sonner';
import { toast } from 'react-toastify';
import { InstagramDMAutomation } from "../../../../../core/services/dm-automation-instagram.service";
import * as gtUtil from "../../../../../core/utility";
import { exceedsLimit, isLimitReached, formatEntitlementLimit, limitAsNumber } from "../../../../../core/utility";
import { IgAutomationCompleteFormData, PostMediaProps, IgAutomationFormData, AutomationRuleEntry, ExistingIgAutomation, ExistingPostMediaProps, AutomationContentType, AutomationScope, ZapTopTabsType, ZapDasboardTabsType, CurrentPlan, PlanEntitlements, ZapLinkProduct } from 'custom-type';
// import { set } from 'react-hook-form';
import { useNavigate } from 'react-router-dom'

// types.ts or near AutomationScope
// export type AutomationContentType = 'post' | 'story' | 'live';

const instagramDMAutomationService: InstagramDMAutomation = new InstagramDMAutomation();

interface SetupWizardProps {
  open: boolean;
  onClose: () => void;
  editingAutomation: ExistingIgAutomation | null;
  instagramAccount: any;
  UserReducer: any;
  refreshAutomationsList: () => Promise<any>;
  setEditingAutomation: React.Dispatch<React.SetStateAction<ExistingIgAutomation | null>>;
  loading: (value: boolean) => void;
  disableLive: boolean;
  contentType?: AutomationContentType;
  scope?: AutomationScope;
  setTopTab: (type: ZapTopTabsType) => void;
  setActiveTab: (type: ZapDasboardTabsType) => void;

  entitlements: PlanEntitlements | null;
  getCurrentPlan: () => Promise<void>;
  totalActiveAutomationPosts: number;
  setTotalActiveAutomationPosts: React.Dispatch<
    React.SetStateAction<number>
  >;
  maxActiveAutomationPosts: number;
  handleOpenUpgradePopup: (upgradeContent: React.ReactNode) => void;
  currentPlanName: string | null;
  allowOtherSource: boolean;
}

const STEP_HELP_SEEN_KEY = 'zap_step_help_seen';

export function SetupWizard(props: SetupWizardProps) {
  const navigate = useNavigate();

  // TODO - revert after story and insta live changes went live.
  // const [contentType, setContentType] = useState<AutomationContentType | null>(null);

  // const [contentType, setContentType] = useState<AutomationContentType | null>('post');

  const [contentType, setContentType] = useState<AutomationContentType>(props.contentType || 'post');
  const [scope, setScope] = useState<AutomationScope>(props.scope || 'multiple');

  const [currentStep, setCurrentStep] = useState(1);
  // const [name, setName] = useState('Untitled Automation');
  const [name, setName] = useState("");
  // const [scope, setScope] = useState<AutomationScope>(null);
  const [rules, setRules] = useState<AutomationRuleEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditting, setIsEditing] = useState(true);

  const [initialScope, setInitialScope] = useState<'all' | 'multiple' | 'single' | null>(null);

  // const totalSteps = scope === 'all' ? 2 : 3;
  // OLD: `scope === 'all'` used to imply ZAP Live, because live was the only trigger
  // saved with that scope — the two tests were equivalent. Now that "Any Post" /
  // "Any Story" also use scope 'all', they would have collapsed the wizard to one
  // step while still needing the rules step, so only content type decides this.
  // const totalSteps = (scope === "all" || contentType === 'live') ? 1 : 2;
  // OLD (pre goal-first flow): const totalSteps = contentType === 'live' ? 1 : 2;

  /* ---------------------------------------------------------------------
     GOAL-FIRST FLOW

     A new Zap now starts from the outcome ("grow my followers") rather than
     from ClanConnect's mechanics, and the answer picks a template that presets
     the rule. The old form is not gone: "Customise details" opens it with
     everything filled in, and editing an existing Zap goes straight to it.

       new Zap  :  1 Goal -> 2 Growth -> 3 Select posts -> 4 Guided questions
       live     :  1 Goal -> 2 Growth -> 3 Guided questions
       editing  :  1 Select posts -> 2 Rule form        (unchanged)
     ------------------------------------------------------------------ */
  const [goal, setGoal] = useState<ZapGoalId | null>(null);
  /* Every response in this Zap, kept as ANSWERS rather than built rules.
     Finished ones used to be banked as AutomationRuleEntry, which made them
     unopenable — the editor speaks answers — so only the last response could be
     changed or previewed. Holding one shape for all of them lets the recap move
     between responses freely. */
  const [goalResponses, setGoalResponses] = useState<ZapGoalAnswers[]>([emptyGoalAnswers()]);
  const [goalZapLinksList, setGoalZapLinksList] = useState<ZapLinkProduct[][]>([[]]);
  const [activeResponse, setActiveResponse] = useState(0);

  const goalAnswers = goalResponses[activeResponse] ?? emptyGoalAnswers();
  const goalZapLinks = goalZapLinksList[activeResponse] ?? [];

  const setGoalAnswers: React.Dispatch<React.SetStateAction<ZapGoalAnswers>> = (update) =>
    setGoalResponses((prev) =>
      prev.map((r, i) =>
        i === activeResponse ? (typeof update === 'function' ? (update as any)(r) : update) : r
      )
    );

  const setGoalZapLinks = (links: ZapLinkProduct[]) =>
    setGoalZapLinksList((prev) => prev.map((l, i) => (i === activeResponse ? links : l)));

  const [goalQuestionIndex, setGoalQuestionIndex] = useState(0);
  // Furthest step reached for this response — the stepper lets you tap back to
  // anything already seen, but not skip ahead past an unanswered question.
  const [goalMaxVisited, setGoalMaxVisited] = useState(0);

  // Creator asked for the full form — the last step renders it instead of the
  // guided questions, pre-filled from whatever they answered.
  const [customising, setCustomising] = useState(false);
  /* Set when a saved Zap could be read back into answers. Editing then uses the
     same guided screens; a Zap that could not be inferred keeps the full form. */
  const [editingGuided, setEditingGuided] = useState(false);

  const usesGoalFlow = !props.editingAutomation || editingGuided;
  /* The two intro screens belong to creating. Editing already has a goal — it
     is what the Zap does — and growth is changed inside the questions there, so
     editing opens straight on the posts it runs on. */
  const hasIntroSteps = usesGoalFlow && !props.editingAutomation;
  const goalStep = hasIntroSteps ? 1 : 0;
  // Growth sits between the goal and the posts: it changes which questions come
  // later, so it has to be answered before those are counted.
  const growthStep = hasIntroSteps ? 2 : 0;
  const totalSteps = (contentType === 'live' ? 1 : 2) + (hasIntroSteps ? 2 : 0);
  const postsStep = contentType === 'live' ? 0 : (hasIntroSteps ? growthStep + 1 : 1);
  const rulesStep = totalSteps;

  const goalTemplate = getGoalTemplate(goal);
  const activeGoalQuestions = goalTemplate ? goalQuestions(goalTemplate, props.entitlements, goalAnswers, { editing: !!props.editingAutomation }) : [];
  const currentGoalQuestion = activeGoalQuestions[goalQuestionIndex];
  const inGoalQuestions = usesGoalFlow && !customising && currentStep === rulesStep;
  const isLastGoalQuestion = inGoalQuestions && goalQuestionIndex >= activeGoalQuestions.length - 1;

  /* ⓘ help for the screen on show. Which screen it is follows the same tests
     the render below uses to pick the step component. */
  const [helpOpen, setHelpOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  // Pulses until the creator has opened help once (remembered in this browser).
  const [helpSeen, setHelpSeen] = useState(() => {
    try { return localStorage.getItem(STEP_HELP_SEEN_KEY) === 'true'; } catch { return true; }
  });
  const helpScreen: StepHelpScreen =
    hasIntroSteps && currentStep === goalStep ? 'goal'
    : hasIntroSteps && currentStep === growthStep ? 'growth'
    : currentStep === postsStep && contentType !== 'live' ? 'posts'
    : inGoalQuestions && currentGoalQuestion ? currentGoalQuestion.id
    : 'rules';
  const helpContent = getStepHelp(helpScreen, { contentType, goal, editing: !!props.editingAutomation });
  const helpKicker = contentType === 'live'
    ? undefined
    : `Step ${currentStep} of ${totalSteps}${inGoalQuestions && currentGoalQuestion ? ` · ${currentGoalQuestion.stepLabel}` : ''}`;
  const openHelp = () => {
    setHelpOpen(true);
    if (!helpSeen) {
      setHelpSeen(true);
      try { localStorage.setItem(STEP_HELP_SEEN_KEY, 'true'); } catch { /* private mode: pulse again next time */ }
    }
  };
  const openGuideFromHelp = () => {
    setHelpOpen(false);
    setGuideOpen(true);
  };

  const progress = (currentStep / totalSteps) * 100;

  // const canProceedFromStep1 = scope !== null && name.trim() !== '';
  // const canProceedFromStep1 =
  // name.trim() !== '' &&
  // (contentType === 'live' || scope !== null);
  // const canProceedFromStep2 = scope === 'all' || selectedPostIds.length > 0;
  // const canFinish = rules?.length > 0 && rules.some((r) => r.is_active);
  const canFinish = name.trim() !== "" && rules.length > 0 && rules.some((r) => r.is_active);

  // New Logic to set automation posts
  const [igId, setIgId] = useState<number>(props.instagramAccount[0].ig_id);

  const [draftAutomation, setDraftAutomation] = useState<IgAutomationCompleteFormData>(
    {
      influencer_id: props.UserReducer.influencer_id,
      ig_id: igId,
      automation_name: 'Untitled Automation',
      automation_media_type: 'post',
      status: 'Live',
      scope: scope ? scope : 'single',
      is_active: true,
      trigger_count: 0,
      automation_posts_data: [],
      automation_rules_data: []
    }
  );

  const [formData, setFormData] = useState<IgAutomationFormData>({
    influencer_id: draftAutomation.influencer_id,
    ig_id: draftAutomation.ig_id,
    automation_name: draftAutomation.automation_name,
    automation_media_type: draftAutomation.automation_media_type,
    status: draftAutomation.status,
    scope: draftAutomation.scope
  });

  const [selectedPosts, setSelectedPosts] = useState<PostMediaProps[]>(
    (draftAutomation.automation_posts_data && draftAutomation.automation_posts_data?.length > 0)
    ?
    draftAutomation.automation_posts_data
    :
    []
  ); // State to track selected posts (multiple)

  const [inactiveCountAdded, setInactiveCountAdded] = useState(false);

  const canProceedFromStep1 = (scope === 'all' || selectedPosts?.length > 0);

  // const canProceedFromStep2 = scope === 'all' || selectedPosts?.length > 0;

  useEffect(() => {
    if (props.editingAutomation) {
      setName(props.editingAutomation.automation_name);
      setScope(props.editingAutomation.scope);
      setContentType(props.editingAutomation.automation_media_type);
      setSelectedPosts(props.editingAutomation.automation_posts_data || []);
      /* The API still echoes the pre-2026-09-01 field names (follow_gate_mode and
         friends) so that web builds and app versions older than the rename keep
         working while the backend ships ahead of them. Strip them here.

         The server treats a payload carrying follow_gate_mode as coming from one
         of those old clients and lets the legacy value win — so if we passed
         these through untouched, this build's own saves would be read as legacy.
         A 'button' rule reads back as follow_gate_mode 'off' (the old UI cannot
         express an opener without a follow gate), which would quietly downgrade
         it to 'instant' on the next save of ANY rule in the automation.

         Safe to delete once the API drops its compatibility block.
         OLD: setRules(props.editingAutomation.automation_rules_data); */
      const savedRules = (props.editingAutomation.automation_rules_data || []).map(
        ({ follow_gate_mode, follow_gate_message, follow_gate_button_label, ...rule }: any) => rule
      );
      setRules(savedRules);

      /* Reopen it in the same screens that built it. A Zap whose rules disagree
         about what it is (a product carousel next to a plain reply) has no single
         goal, so inference returns null and it keeps the full form — which can
         express anything the guided flow cannot. */
      const inferred = inferGoalFromRules(savedRules);
      if (inferred) {
        setGoal(inferred.goal);
        setGoalResponses(inferred.responses);
        setGoalZapLinksList(inferred.zapLinks);
        setActiveResponse(0);
        setGoalQuestionIndex(0);
        setGoalMaxVisited(0);
        setCustomising(false);
      }
      setEditingGuided(!!inferred);

      setCurrentStep(1);

      setInitialScope(props.editingAutomation.scope);
    } else {
      // set isEditing(false);
      setIsEditing(false);
      resetWizard();
    }
  }, [props.open]);

  const resetWizard = () => {
    setCurrentStep(1);
    // setName('Untitled Automation');
    setName('');
    // setScope(null);
    setScope(props.scope || null);
    // TODO - revert after story and insta live changes went live.
    // setContentType(null);
    // setContentType('post');
    setContentType(props.contentType || null);
    setRules([]);
    setSelectedPosts([]);

    setInitialScope(null); // default create mode

    setInactiveCountAdded(false);

    setGoal(null);
    setGoalResponses([emptyGoalAnswers()]);
    setGoalZapLinksList([[]]);
    setActiveResponse(0);
    setGoalQuestionIndex(0);
    setGoalMaxVisited(0);
    setCustomising(false);
    setEditingGuided(false);
    setCreating(false);
  };

  // const handleNext = () => {
  //   if (currentStep === 1 && canProceedFromStep1) {
  //     // New Logic for skipping step 2 for insta live
  //     if (contentType === 'live') {
  //       // For 'live' content type, skip post selection
  //       setSelectedPosts([]); // No posts to select
  //       setCurrentStep(3); // Skip step 2 Move directly to rules configuration
  //       return;
  //     }
  //     else if (['story', 'post'].includes(contentType || '')) {
  //       // If user selects "all"
  //       if (scope === 'all') {
  //         // Always reset posts
  //         setSelectedPosts([]);
  //         setCurrentStep(3);  // Skip step 2
  //         return;
  //       }

  //       // If user selects "single"
  //       else if (scope === 'single') {
  //         // If multiple posts already selected → reset
  //         if (selectedPosts.length > 1) {
  //           // Keep ONLY the first selected post
  //           setSelectedPosts([selectedPosts[0]]);
  //         }
  //         setCurrentStep(2);
  //         return;
  //       } else {
  //         setCurrentStep(2);
  //       }
  //     }
  //   }
  //   else if (currentStep === 2 && canProceedFromStep2) {
  //     setCurrentStep(3);
  //   }
  // };

  // const handleNext = () => {
  //   if (currentStep === 1 && canProceedFromStep1) {
  //     // New Logic for skipping step 1 for insta live
  //     if (contentType === 'live') {
  //       // For 'live' content type, skip post selection
  //       setSelectedPosts([]); // No posts to select
  //       setCurrentStep(2); // Skip step 1 Move directly to rules configuration
  //       return;
  //     }
  //     else if (['story', 'post'].includes(contentType || '')) {
  //       setCurrentStep(2);
  //     }
  //   }
  // };
  
  const handleNext = () => {
    // Goal step — nothing to validate beyond having picked one.
    if (hasIntroSteps && currentStep === goalStep) {
      if (!goal) return;
      setCurrentStep(growthStep);
      return;
    }

    // Growth step — always answered ("no" is the default), so it never blocks.
    if (hasIntroSteps && currentStep === growthStep) {
      setCurrentStep(contentType === 'live' ? rulesStep : postsStep);
      if (contentType === 'live') setSelectedPosts([]);
      return;
    }

    // Guided questions all live on the last step; Next walks through them.
    if (inGoalQuestions) {
      if (!isLastGoalQuestion) {
        setGoalQuestionIndex((i) => {
          setGoalMaxVisited((max) => Math.max(max, i + 1));
          return i + 1;
        });
      }
      return;
    }

    if (currentStep === postsStep && canProceedFromStep1) {

      // 🔥 ADD THIS VALIDATION BLOCK
      if (scope !== "all" && contentType !== "live") {

        const inactiveSelectedPostsCount = selectedPosts.filter(
          (post: any) => post.is_active === 0
        ).length;

        const effectiveCount =
          props.totalActiveAutomationPosts + inactiveSelectedPostsCount;

        // OLD (pre-unlimited): if (effectiveCount > props.maxActiveAutomationPosts) {
        if (exceedsLimit(effectiveCount, props.maxActiveAutomationPosts)) {
          // handleClose();
          props.handleOpenUpgradePopup(
            <>
              You can have up to {formatEntitlementLimit(props.maxActiveAutomationPosts)} active automations. 
              To add more, please delete or pause an existing automation
              {
                props.currentPlanName === 'Savings'
                ?
                <span>.</span>
                :
                <span>
                  , or{" "}
                  <span
                    className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                    onClick={() => navigate(zapPaths().subscription)}
                  >
                    upgrade 
                  </span> your plan.
                </span>
              }
            </>
          );
          return;
        }
      }

      // Existing logic
      if (contentType === 'live') {
        setSelectedPosts([]);
        setCurrentStep(rulesStep);
        return;
      }

      setCurrentStep(rulesStep);
    }
  };

  // const handleBack = () => {
  //   // From Step 3 → Step 1 (Insta Live OR scope = all)
  //   if (currentStep === 3 && (contentType === 'live' || scope === 'all')) {
  //     setCurrentStep(1);
  //     return;
  //   } 
  //   else if (currentStep > 1) {
  //     setCurrentStep(currentStep - 1);
  //   }
  // };

  const handleBack = () => {
    // The full form was opened from the guided questions — go back to them
    // rather than skipping the whole step.
    if (customising) {
      setCustomising(false);
      return;
    }

    // Walk back through the guided questions before leaving the step.
    if (inGoalQuestions && goalQuestionIndex > 0) {
      setGoalQuestionIndex((i) => i - 1);
      return;
    }

    if (currentStep > 1) {
      // Live has no post-selection step, so the rules step falls back to growth.
      setCurrentStep(currentStep === rulesStep && contentType === 'live' && hasIntroSteps ? growthStep : currentStep - 1);
      return;
    }
    // Step 1 has no earlier step inside the wizard, but it is not the start of the
    // journey: the user came from the content-type picker (ZAP Post/Reel | Story | Live)
    // on the page behind this dialog. Back used to be dead here, leaving Cancel / X as
    // the only way out — which reads as "discard", not "go back a step".
    handleClose();
  };

  const submitAutomation = async () => {
    if (!canFinish) return;
    if (isSubmitting) return;       // 🔒 stops duplicate submissions
    setIsSubmitting(true);          // 🔒 lock
    props.loading(true);
    try {
      if (props.editingAutomation) {

        let res: any;
        res = await instagramDMAutomationService.igAutomationUpdate(props.editingAutomation);
        if (res.status === gtUtil.Constants.SUCCESS) {
          props.refreshAutomationsList();

          // toast.success("Automation updated successfully");
          toast.success(res.info);
        } else {
          toast.error(res.info);
        }
      }
      else {
        let res: any;
        res = await instagramDMAutomationService.igAutomationSave(draftAutomation);

        if (res.status === gtUtil.Constants.SUCCESS) {
          // setAutomations([...automations, newAutomation]);
          props.refreshAutomationsList();
          toast.success(res.info);
          // toast.success("Automation created successfully");
        } else {
          toast.error(res.info);
        }
      }
      resetWizard();
      if (props.editingAutomation) {
        props.onClose();
      } else {
        props.onClose();
        props.setTopTab('zaps');
        props.setActiveTab('automations');
        // navigate(`/influencer/zap/automations/ig-dm-automations`);
      }
    } catch (error) {
      console.error('Error in submitAutomation:', error);
      toast.error('An error occurred while saving the automation.');
    } finally {
      props.loading(false);
      setIsSubmitting(false);      // unlock
      setCreating(false);
    }
  };

  /* -------------------------------------------------------------------------
     "UNSAVED RULE" GATE

     The rule form in step 2 is staging: keywords, replies and messages only
     reach `rules` when Add/Update Rule is pressed. Going straight for
     Create/Update Automation with the form open used to throw all of it away
     without a word. The footer now settles the step first — pending "+" text is
     committed automatically (StepConfigureRulesHandle.flushPendingDrafts), and
     the one genuinely ambiguous case, a whole rule left open, is asked about
     rather than guessed at.
     ---------------------------------------------------------------------- */
  const stepRulesRef = useRef<StepConfigureRulesHandle>(null);
  /* The dialog body is the scroll container. A new question is a new screen and
     must start at its input, not wherever the previous (taller) screen was
     scrolled to — otherwise Next lands you mid-preview with the field above the
     fold. Mirrors the ScrollView reset in the app's SetupWizard. */
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const [openRuleGate, setOpenRuleGate] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(false);

  /* Create Automation became async when it started saving forms first, which
     opened a window: `isSubmitting` is not set until submitAutomation runs a
     render later, so a second tap in between re-entered the whole flow and
     created a duplicate form. The ref is checked and set synchronously, so the
     second tap of a double tap — same tick, before any re-render — is refused;
     the state mirror is only there to grey the button out. */
  const creatingRef = useRef(false);
  const [isCreating, setIsCreating] = useState(false);
  const setCreating = (value: boolean) => {
    creatingRef.current = value;
    setIsCreating(value);
  };

  const handleFinish = () => {
    if (!canFinish || isSubmitting) return;

    /* Blur normally commits these before the click ever lands; this is the
       belt-and-braces path for a field that never held focus. */
    stepRulesRef.current?.flushPendingDrafts();

    if (stepRulesRef.current?.hasOpenRule()) {
      setOpenRuleGate(true);
      return;
    }
    submitAutomation();
  };

  /* Adding the rule runs setRules, which only reaches the request payload one
     render later via the [rules] effect above. Rather than guess at that timing,
     wait for the payload to literally hold the same array and submit then. If it
     never matches, nothing breaks: the dialog is already closed and the rule is
     saved, so the creator just presses the button again. */
  useEffect(() => {
    if (!pendingFinish) return;
    const payloadRules = props.editingAutomation
      ? props.editingAutomation.automation_rules_data
      : draftAutomation.automation_rules_data;
    if (payloadRules !== rules) return;
    setPendingFinish(false);
    submitAutomation();
  }, [pendingFinish, rules, draftAutomation, props.editingAutomation]);

  const handleGateSaveRule = () => {
    // Refused (missing keyword, bad link, plan limit) — it has already said why,
    // so leave the dialog open on the rule the creator still has to fix.
    if (!stepRulesRef.current?.commitOpenRule()) return;
    setOpenRuleGate(false);
    setPendingFinish(true);
  };

  useEffect(() => {
    dialogBodyRef.current?.scrollTo({ top: 0 });
  }, [goalQuestionIndex, currentStep]);

  const handleGateDiscardRule = () => {
    // Discarding never touches `rules`, so the payload is already correct.
    stepRulesRef.current?.discardOpenRule();
    setOpenRuleGate(false);
    submitAutomation();
  };

  /* ------------------------------------------------------------------
     GOAL TEMPLATES -> a rule

     The template holds everything the creator was not asked about (opener
     copy, follow mode, DM vs Zap Link), so building the rule is the only
     place those presets are applied. Creating and customising share it, which
     is what makes "Customise details" open exactly what was about to be saved.
     --------------------------------------------------------------------- */
  const buildGoalRule = (index: number = activeResponse): AutomationRuleEntry | null => {
    if (!goalTemplate) return null;
    const answers = goalResponses[index];
    if (!answers) return null;
    return buildRuleFromGoal(goalTemplate, answers, {
      entitlements: props.entitlements,
      zapLinks: goalZapLinksList[index] || [],
      ruleId: index + 1,
    });
  };

  /** Every response in this Zap, in the order they were added. */
  const buildAllGoalRules = (): AutomationRuleEntry[] =>
    goalResponses
      .map((_, i) => buildGoalRule(i))
      .filter((r): r is AutomationRuleEntry => !!r)
      .map((r, i) => ({ ...r, ig_automation_rule_id: i + 1 }));

  const handleCustomise = () => {
    const rules = buildAllGoalRules();
    if (!rules.length || !goalTemplate) return;
    setRules(rules);
    if (!name.trim()) setName(buildAutomationName(goalTemplate, goalAnswers));
    setCustomising(true);
  };

  /* "Answer a different word too" — bank the response just built and start the
     questions again for the next one, inside the same Zap. */
  const handleAddAnotherResponse = () => {
    setGoalResponses((prev) => [...prev, emptyGoalAnswers()]);
    setGoalZapLinksList((prev) => [...prev, []]);
    setActiveResponse(goalResponses.length);
    setGoalQuestionIndex(0);
    setGoalMaxVisited(0);
  };

  /* Removing the response currently open moves the recap to a neighbour rather
     than leaving activeResponse pointing past the end of the list. */
  const handleRemoveResponse = (index: number) => {
    if (goalResponses.length <= 1) return;
    setGoalResponses((prev) => prev.filter((_, i) => i !== index));
    setGoalZapLinksList((prev) => prev.filter((_, i) => i !== index));
    setActiveResponse((current) => (index < current || current === goalResponses.length - 1 ? Math.max(0, current - 1) : current));
  };

  /* Create from the guided flow. setRules only reaches the request payload one
     render later, so this reuses the wizard's existing pendingFinish handshake
     rather than racing it. */
  /* A response collecting sign-ups through a built form has no link yet: the
     form is created here, and its public /form/:slug becomes the button's URL.
     Done before the rules are built so the saved rule carries a real link. */
  const persistGoalForms = async (): Promise<ZapGoalAnswers[] | null> => {
    const owner = {
      user_id: props.UserReducer.user_id,
      account_id: props.UserReducer.account_id,
      influencer_id: props.UserReducer.influencer_id,
    };

    const saved: ZapGoalAnswers[] = [];
    for (const answers of goalResponses) {
      if (answers.signup_mode !== 'form') { saved.push(answers); continue; }

      try {
        const res: any = await ZapFormService.saveForm(formSavePayload(answers, owner));
        if (res?.status !== 1 || !res?.data?.slug) {
          toast.error(res?.info || 'Could not save your form — please try again');
          return null;
        }
        saved.push({
          ...answers,
          zap_form_id: res.data.zap_form_id || answers.zap_form_id,
          form_slug: res.data.slug,
          link_url: `${BASE_URL_UI}/form/${res.data.slug}`,
          link_label: formButtonLabel(answers),
        });
      } catch {
        toast.error('Could not save your form — please try again');
        return null;
      }
    }
    return saved;
  };

  const handleCreateFromGoal = async () => {
    if (!goalTemplate || isSubmitting || creatingRef.current) return;
    setCreating(true);

    const savedResponses = await persistGoalForms();
    if (!savedResponses) {                // the toast has already explained why
      setCreating(false);
      return;
    }

    // Keep the saved ids/links so a retry updates the same form rather than
    // creating a second one.
    setGoalResponses(savedResponses);

    const rules = savedResponses
      .map((answers, i) =>
        buildRuleFromGoal(goalTemplate, answers, {
          entitlements: props.entitlements,
          zapLinks: goalZapLinksList[i] || [],
          ruleId: i + 1,
        })
      )
      .map((r, i) => ({ ...r, ig_automation_rule_id: i + 1 }));

    if (!rules.length) return;
    setRules(rules);
    setName(name.trim() || buildAutomationName(goalTemplate, goalAnswers));
    setPendingFinish(true);
  };

  const handleClose = () => {
    resetWizard();
    props.onClose();
  };

  /**
   * Leave the wizard and land on the subscription screen — the same destination the
   * Zap hub's locked cards use (ZapHub.handleCardClick). The wizard is a Dialog, so it
   * is closed first rather than left mounted over the new route.
   */
  const handleGoToUpgrade = () => {
    handleClose();
    navigate(zapPaths().subscription);
  };

  // const handlePostClick = (media: PostMediaProps, scope: AutomationScope) => {
  //   if (!media['media_id'] && media['id']) {
  //     media['media_id'] = media['id'];
  //   }
  //   // --- Single Select ----
  //   if (scope === "single") {
  //     setSelectedPosts([media]);   // ⬅️ Always REPLACE
  //     return;
  //   }

  //   // --- Multiple Toggle ----
  //   setSelectedPosts((prev) => {
  //     const exists = prev.some((post) => post.shortcode === media.shortcode);

  //     if (exists) {
  //       return prev.filter((post) => post.shortcode !== media.shortcode);
  //     } else {
  //       return [...prev, media];
  //     }
  //   });
  // };

  const handlePostClick = (media: PostMediaProps, scope: AutomationScope) => {
    if (!media['media_id'] && media['id']) {
      media['media_id'] = media['id'];
    }

    // --- Single Select ----
    if (scope === "single") {
      const wasAlreadySelected =
        selectedPosts.length === 1 &&
        selectedPosts[0].shortcode === media.shortcode;

      if (!wasAlreadySelected) {
        if (
          selectedPosts.length === 0 &&
          // OLD (pre-unlimited): props.totalActiveAutomationPosts >= props.maxActiveAutomationPosts
          isLimitReached(props.totalActiveAutomationPosts, props.maxActiveAutomationPosts)
        ) {
          props.handleOpenUpgradePopup(
            <>
              You can have up to {formatEntitlementLimit(props.maxActiveAutomationPosts)} active automations. 
              To add more, please delete or pause an existing automation
              {
                props.currentPlanName === 'Savings'
                ?
                <span>.</span>
                :
                <span>
                  , or{" "}
                  <span
                    className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                    onClick={() => navigate(zapPaths().subscription)}
                  >
                    upgrade 
                  </span> your plan.
                </span>
              }
            </>
          );
          // handleClose();
          return;
        }
        
        // If replacing an existing selection, no count change
        if (selectedPosts.length === 0) {
          props.setTotalActiveAutomationPosts(prev => prev + 1);
        }
      }

      setSelectedPosts([media]);
      return;
    }

    // --- Multiple Toggle ----
    setSelectedPosts((prev) => {
      const exists = prev.some(
        (post) => post.shortcode === media.shortcode
      );

      if (exists) {
        const postToRemove: any = prev.find(
          (post) => post.shortcode === media.shortcode
        );

        // 🔥 Only decrease if the post was newly added (active)
        if (postToRemove?.is_active !== 0) {
          props.setTotalActiveAutomationPosts((count) =>
            Math.max(0, count - 1)
          );
        }

        return prev.filter(
          (post) => post.shortcode !== media.shortcode
        );
      }
      else {

        const inactiveSelectedPostsCount = prev.filter(
          (post: any) => post.is_active === 0
        ).length;

        const effectiveCount =
          props.totalActiveAutomationPosts + inactiveSelectedPostsCount;

        // OLD (pre-unlimited): if (effectiveCount >= props.maxActiveAutomationPosts) {
        if (isLimitReached(effectiveCount, props.maxActiveAutomationPosts)) {
          props.handleOpenUpgradePopup(
            <>
              You can have up to {formatEntitlementLimit(props.maxActiveAutomationPosts)} active automations. 
              To add more, please delete or pause an existing automation
              {
                props.currentPlanName === 'Savings'
                ?
                <span>.</span>
                :
                <span>
                  , or{" "}
                  <span
                    className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                    onClick={() => navigate(zapPaths().subscription)}
                  >
                    upgrade 
                  </span> your plan.
                </span>
              }
            </>
          );
          // handleClose();
          return prev;
        }

        props.setTotalActiveAutomationPosts((count) =>
          count + 1
        );

        return [...prev, media];
      }
    });
  };
  
  // Step 2 to set posts
  // const [loading, setLoading] = useState(false);

  useEffect(() => {
    // setAutomationName(props.automationTitle);
    setFormData({...formData, automation_name: name, scope: scope, ig_id: igId});

    if (props.editingAutomation) {
      props.setEditingAutomation(prev => {
        if (!prev) return prev; // ← prevents null crash
        return {
          ...prev,
          ig_id: igId,
          automation_name: name,
          scope: scope
        }
      });
    } else {
      setDraftAutomation(prev => ({
        ...prev,
        ig_id: igId,
        automation_name: name,
        scope: scope,
        automation_media_type: contentType ? contentType : 'post'
      }));
    }
  }, [name, scope, igId, contentType]);

  useEffect(() => {
    if (!isEditting)
      setSelectedPosts([]);
  }, [contentType]);

  // Switching to "Any Post" / "Any Story" drops whatever specific media was picked.
  // A catch-all zap must save with no ig_automation_posts rows — leaving stale
  // selections in the payload would register those posts and make the automation
  // behave as a specific one on top of being catch-all.
  useEffect(() => {
    if (scope === 'all' && contentType !== 'live') {
      setSelectedPosts([]);
    }
  }, [scope, contentType]);

  useEffect(() => {
    if (props.editingAutomation) {
      props.setEditingAutomation(prev => {
        if (!prev) return prev; // ← prevents null crash
        return {
          ...prev,
          // automation_posts_data: selectedPosts
          automation_posts_data: selectedPosts as ExistingPostMediaProps[]
        }
      });
    } else {
      setDraftAutomation(prev => ({
        ...prev,
        automation_posts_data: selectedPosts
      }));
    }
  }, [selectedPosts]);


  // Step 3 to set rules

  useEffect(() => {
    if (props.editingAutomation) {
      props.setEditingAutomation(prev => {
        if (!prev) return prev; // ← prevents null crash
        return {
          ...prev,
          automation_rules_data: rules
        }
      });
    } else {
      setDraftAutomation(prev => ({
        ...prev,
        automation_rules_data: rules
      }));
    }
  }, [rules]);

  useEffect(() => {
    props.getCurrentPlan();
  }, []);

  useEffect(() => {
    if (!props.editingAutomation) return;
    if (inactiveCountAdded) return;

    const inactiveSelectedPostsCount = selectedPosts.filter(
      (post: any) => post.is_active === 0
    ).length;

    if (inactiveSelectedPostsCount > 0) {
      props.setTotalActiveAutomationPosts(prev =>
        prev + inactiveSelectedPostsCount
      );
      setInactiveCountAdded(true);
    }
  }, [selectedPosts, props.editingAutomation]);

  return (
    <Dialog defaultOpen={true} open={props.open} onOpenChange={(open) => {
    if (!open) handleClose();
  }}>
      {/* DialogContent is a CSS grid whose implicit column sizes to its widest
          content, so a long unbroken URL (e.g. the "Link button" recap) widened
          the whole wizard past the screen. minmax(0,1fr) pins the column to the
          dialog's width so truncate / wrapping inside it actually applies. */}
      <DialogContent
        ref={dialogBodyRef}
        // className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]"
        style={{transform:'inherit'}}
      >
        <DialogHeader>
          {currentStep === rulesStep &&
            <DialogTitle>
              {isEditting ? 'Edit Zap' : 'Create New Zap'}
            </DialogTitle>
          }
          {/* OLD: the step count alone. The ⓘ sits right after it (on Live, which
              has no step count, it stands alone). */}
          <div className="flex items-center gap-2">
            { contentType !== 'live' &&
              <DialogDescription>
                Step {currentStep} of {totalSteps}
              </DialogDescription>
            }
            <StepHelpButton onClick={openHelp} pulse={!helpSeen} />
          </div>
          { contentType !== 'live' &&
            <Progress value={progress} className="mt-2" />
          }
        </DialogHeader>

        <div className="py-6">
          {/* {currentStep === 1 && (
            <StepScope
              name={name}
              setName={setName}
              contentType={contentType}
              setContentType={setContentType}
              scope={scope}
              setScope={setScope}
              isEditting={isEditting}
              disableLive={props.disableLive}
            />
          )} */}

          {/* {currentStep === 2 && (
            <StepSelectPosts
              selectedPosts={selectedPosts}
              scope={scope!}
              contentType={contentType!}
              instagramAccount={props.instagramAccount}
              handlePostClick={handlePostClick}
            />
          )}

          {currentStep === 3 && (
            <StepConfigureRules
              rules={rules}
              setRules={setRules}
              contentType={contentType!}
            />
          )} */}

          {hasIntroSteps && currentStep === goalStep && (
            <StepGoal
              contentType={contentType}
              goal={goal}
              setGoal={setGoal}
              entitlements={props.entitlements}
              handleGoToUpgrade={handleGoToUpgrade}
            />
          )}

          {hasIntroSteps && currentStep === growthStep && (
            <StepGrowth
              followGate={goalAnswers.follow_gate}
              setFollowGate={(value) => setGoalAnswers((prev) => ({ ...prev, follow_gate: value }))}
              entitlements={props.entitlements}
              handleGoToUpgrade={handleGoToUpgrade}
            />
          )}

          {currentStep === postsStep && contentType !== 'live' && (
            <StepSelectPosts
              selectedPosts={selectedPosts}
              scope={scope!}
              setScope={setScope}
              entitlements={props.entitlements}
              handleGoToUpgrade={handleGoToUpgrade}
              contentType={contentType!}
              instagramAccount={props.instagramAccount}
              handlePostClick={handlePostClick}
              setTopTab={props.setTopTab}
              onClose={props.onClose}
              setActiveTab={props.setActiveTab}
              totalActiveAutomationPosts={props.totalActiveAutomationPosts}
              setTotalActiveAutomationPosts={props.setTotalActiveAutomationPosts}
              maxActiveAutomationPosts={props.maxActiveAutomationPosts}
            />
          )}

          {inGoalQuestions && currentGoalQuestion && goalTemplate && (
            <StepGoalSetup
              template={goalTemplate}
              question={currentGoalQuestion}
              questionNumber={goalQuestionIndex + 1}
              questionCount={activeGoalQuestions.length}
              questions={activeGoalQuestions}
              maxVisitedIndex={goalMaxVisited}
              onJumpToQuestion={setGoalQuestionIndex}
              answers={goalAnswers}
              setAnswers={setGoalAnswers}
              entitlements={props.entitlements}
              ctaOwner={{ influencer_id: props.UserReducer.influencer_id, account_id: props.UserReducer.account_id }}
              canBuildForms={!!props.entitlements?.zap_forms_enabled}
              handleGoToUpgrade={handleGoToUpgrade}
              zapLinks={goalZapLinks}
              setZapLinks={setGoalZapLinks}
              maxZapProducts={limitAsNumber(props.entitlements?.zap_link_limit, 0)}
              handleOpenUpgradePopup={props.handleOpenUpgradePopup}
              onClose={props.onClose}
              currentPlanName={props.currentPlanName}
              allowOtherSource={props.allowOtherSource}
              zapProductImageUploadPath={`accounts/${props.UserReducer.account_id}/influencer/${props.UserReducer.influencer_id}/zap_link_product`}
              onCustomise={handleCustomise}
              onAddAnotherResponse={handleAddAnotherResponse}
              ruleCount={goalResponses.length}
              activeResponse={activeResponse}
              onSelectResponse={setActiveResponse}
              onRemoveResponse={handleRemoveResponse}
              responseSummaries={goalResponses.map((_, i) => {
                const rule = buildGoalRule(i);
                return rule ? summariseGoalRule(rule, rule.keywords || []) : [];
              })}
              summaryLines={
                currentGoalQuestion.kind === 'review'
                  ? summariseGoalRule(
                      buildRuleFromGoal(goalTemplate, goalAnswers, {
                        entitlements: props.entitlements,
                        zapLinks: goalZapLinks,
                      }),
                      goalAnswers.keywords
                    )
                  : []
              }
            />
          )}

          {currentStep === rulesStep && (!usesGoalFlow || customising) && (
            <StepConfigureRules
              ref={stepRulesRef}
              name={name}
              setName={setName}
              rules={rules}
              setRules={setRules}
              contentType={contentType!}
              entitlements={props.entitlements}
              ctaOwner={{ influencer_id: props.UserReducer.influencer_id, account_id: props.UserReducer.account_id }}
              handleOpenUpgradePopup={props.handleOpenUpgradePopup}
              handleGoToUpgrade={handleGoToUpgrade}
              onClose={props.onClose}
              currentPlanName={props.currentPlanName}
              allowOtherSource={props.allowOtherSource}
              zapProductImageUploadPath={`accounts/${props.UserReducer.account_id}/influencer/${props.UserReducer.influencer_id}/zap_link_product`}
            />
          )}
        </div>

        {/* <div className="flex justify-between pt-4 border-t"> */}
        {/* max-md:mb-4 — on phones the fixed bottom tab bar clipped the buttons slightly. */}
        <div className="flex justify-between pt-4 border-t max-md:mb-4">
          {/* OLD: disabled on step 1, so there was no way back to the content-type picker.
              disabled={currentStep === 1} */}
          <Button
            variant="outline"
            onClick={handleBack}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} className='max-md:hidden'>
              Cancel
            </Button>

            {(currentStep < totalSteps || (inGoalQuestions && !isLastGoalQuestion)) ? (
              <Button variant="outline"
              className='btn btn-primary'
                onClick={handleNext}
                disabled={
                  (hasIntroSteps && currentStep === goalStep && !goal) ||
                  (currentStep === postsStep && !canProceedFromStep1) ||
                  (inGoalQuestions && !!currentGoalQuestion &&
                    !isGoalQuestionAnswered(currentGoalQuestion, goalAnswers, goalZapLinks.length))
                }
              >
                {inGoalQuestions && currentGoalQuestion?.optional &&
                !isGoalQuestionAnswered({ ...currentGoalQuestion, optional: false }, goalAnswers, goalZapLinks.length)
                  ? 'Skip'
                  : 'Next'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                variant="outline"
                className='btn btn-primary'
                type="button"
                onClick={inGoalQuestions ? handleCreateFromGoal : handleFinish}
                disabled={
                  inGoalQuestions
                    ? !activeGoalQuestions.every((q) =>
                        isGoalQuestionAnswered(q, goalAnswers, goalZapLinks.length)
                      ) || isSubmitting || isCreating
                    : !canFinish || isSubmitting
                }
              >
                {isCreating || isSubmitting
                  ? 'Creating…'
                  // OLD: : (isEditting ? 'Update Automation' : 'Create Automation')}
                  : (isEditting ? 'Confirm Update' : 'Go Live')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Asked, not guessed: an open rule form holds a keyword list, replies, a
          message and possibly Zap Link products, and silently adopting all of
          that on submit is as surprising as silently dropping it. The small "+"
          fields need no dialog — those commit themselves on blur. */}
      <AlertDialog open={openRuleGate} onOpenChange={setOpenRuleGate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This rule isn't added yet</AlertDialogTitle>
            <AlertDialogDescription>
              You still have a rule open. Add it to this automation, or discard it and
              carry on with the rules you have already added.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setOpenRuleGate(false)}>
              Keep editing
            </Button>
            <Button variant="outline" onClick={handleGateDiscardRule}>
              Discard rule
            </Button>
            <Button variant="outline" className="btn btn-primary" onClick={handleGateSaveRule}>
              Add rule &amp; {isEditting ? 'update' : 'create'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StepHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        kicker={helpKicker}
        content={helpContent}
        onOpenGuide={openGuideFromHelp}
      />
      <StepGuideDialog open={guideOpen} onOpenChange={setGuideOpen} />
    </Dialog>
  );
}